import { ChevronRight, ExternalLink, Trash2, X } from 'lucide-react';
import * as React from 'react';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { AInputSearch } from '../../atoms/filter/InputSearchFilter';
import { Field } from '../../atoms/forms';
import Input from '../../components_v2/input/Input';
import { creationPopupInputStyle } from '../../components_v2/popup/style/CreationFormStyle';
import { ButtonStyle } from '../../components_v2/popup/PopupCreation';
import { DefaultButton } from '../../styles/global/css/GlobalButton';
import { BorderColor, DarkGreySidely2, PastelPrimaryColor, PrimaryColor, RedSidely, SidelyBlack, SurfaceColor } from '../../styles/global/css/Utils';
import { translateToString } from '../../styles/global/translate';
import { AlertRes } from '../alert/AlertProvider';
import useAlert from '../alert/UseAlert';
import { AdditionalValuesPath } from '../settings/subSettings/actions';
import { BottomActionButtonsWrapper, BottomActionButtonsWrapperWrapper, NumberContainer } from '../client-companies/BottomActionButtons';
import { BoolCheckbox } from '../forms/PopupField';
import NoData from '../noData/NoData';
import { FlexDiv } from './style';
import { TitleFontFamily } from '../../styles/global/css/GlobalText';

export type AdditionalValuesView = [Record<string, unknown>, number];
export type DimensionValue = { field: Field, value: unknown };

function viewDimensionIds([view]: AdditionalValuesView): number[] {
	return Object.keys(view).filter(key => key !== 'f').map(Number);
}

export function matchingPath(view: AdditionalValuesView, paths: AdditionalValuesPath[]): AdditionalValuesPath | undefined {
	const ids = viewDimensionIds(view).sort((a, b) => a - b);
	return paths.find(p => p.field_id === view[0]['f'] && p.additional_column_ids.length === ids.length && [...p.additional_column_ids].sort((a, b) => a - b).every((id, i) => id === ids[i]));
}

type TreeNode = {
	key: string,
	chain: DimensionValue[],
	ownCount: number,
	children: TreeNode[],
};

const Card = styled.div`
	background-color: ${SurfaceColor};
	border: 1px solid ${BorderColor};
	border-radius: 8px;
	flex: 1 0 auto;
`;

const Separator = styled.hr`
	margin: 0;
	border: none;
	border-top: 1px solid ${BorderColor};
`;

const FieldTitle = styled.h2`
	margin: 0;
	font-size: 16px;
	font-weight: 600;
	color: ${SidelyBlack};
	font-family: ${TitleFontFamily};
`;

const Row = styled.div<{ level: number }>`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 10px 14px 10px ${p => 14 + p.level * 24}px;
	border-bottom: 1px solid ${BorderColor};
	background-color: ${p => p.level > 0 ? `color-mix(in oklab, ${SidelyBlack} ${Math.min(p.level * 1.5, 30)}%, transparent)` : SurfaceColor};
`;

const Details = styled.details`
	& > summary {
		list-style: none;
		cursor: pointer;
	}
	& > summary::-webkit-details-marker {
		display: none;
	}
	& > summary svg.chevron {
		transition: transform 0.15s;
	}
	&[open] > summary svg.chevron {
		transform: rotate(90deg);
	}
`;

const ChevronSlot = styled.div`
	width: 16px;
	flex-shrink: 0;
	display: flex;
	color: ${DarkGreySidely2};
`;

const Chips = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 6px;
	flex: 1;
`;

const Chip = styled.div`
	display: flex;
	align-items: center;
	gap: 5px;
	height: 26px;
	padding: 0 8px;
	border-radius: 5px;
	font-size: 12px;
	white-space: nowrap;
	color: ${DarkGreySidely2};
	& > b {
		color: ${SidelyBlack};
		font-weight: 500;
	}
	& > b::before {
		content: '';
		display: inline-block;
		width: 1.5px;
		height: 16px;
		margin-right: 5px;
		vertical-align: middle;
		background-color: ${BorderColor};
	}
`;

const ChildrenBadge = styled.span < { bgColor: string, color: string }>`
	flex-shrink: 0;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 22px;
	height: 22px;
	aspect-ratio: 1;
	padding: 0 4px;
	box-sizing: border-box;
	border-radius: 50%;
	background-color: ${p => p.bgColor};
	color: ${p => p.color};
	font-size: 11px;
	font-weight: 500;
	line-height: 1;
`;

const RowButton = styled.button`
	display: flex;
	align-items: center;
	gap: 5px;
	height: 28px;
	padding: 0 10px;
	border: 1px solid ${BorderColor};
	border-radius: 6px;
	background-color: ${SurfaceColor};
	color: ${SidelyBlack};
	font-size: 12px;
	cursor: pointer;
	& svg {
		color: inherit;
		stroke: currentColor;
	}
	&:hover,
	&:hover svg {
		border-color: ${PrimaryColor};
		color: ${PrimaryColor};
		stroke: ${PrimaryColor};
	}
`;

const TrashButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 28px;
	width: 28px;
	padding: 0;
	border: none;
	background: none;
	color: ${DarkGreySidely2};
	cursor: pointer;
	& svg {
		stroke: currentColor;
	}
	&:hover {
		color: ${RedSidely};
	}
`;

type MatchedView = { view: AdditionalValuesView, path: AdditionalValuesPath };

function lastStep(node: TreeNode): DimensionValue {
	return node.chain[node.chain.length - 1];
}

function orderedDimensions({ view, path }: MatchedView, fieldsMap: Map<number, Field>): DimensionValue[] | undefined {
	const chain: DimensionValue[] = [];
	for (const id of path.additional_column_ids) {
		const field = fieldsMap.get(id);
		if (!field) return undefined;
		chain.push({ field, value: view[0][id.toString()] });
	}
	return chain;
}

function buildTree(matched: MatchedView[], fieldsMap: Map<number, Field>): TreeNode[] {
	const roots: TreeNode[] = [];
	for (const item of matched) {
		const chain = orderedDimensions(item, fieldsMap);
		if (!chain || chain.length === 0) continue;
		let siblings = roots;
		let node: TreeNode | undefined;
		let parentKey = '';
		chain.forEach((step, depth) => {
			const key = `${parentKey}${step.field.id}:${String(step.value)}`;
			parentKey = `${key}/`;
			node = siblings.find(n => n.key === key);
			if (!node) {
				node = { key, chain: chain.slice(0, depth + 1), ownCount: 0, children: [] };
				siblings.push(node);
			}
			siblings = node.children;
		});
		if (node) node.ownCount += item.view[1];
	}
	const sortTree = (nodes: TreeNode[]): TreeNode[] => nodes
		.map(node => ({ ...node, children: sortTree(node.children) }))
		.sort((a, b) => lastStep(a).field.name.localeCompare(lastStep(b).field.name) || String(lastStep(a).value).localeCompare(String(lastStep(b).value)));
	return sortTree(roots);
}

function filterTree(nodes: TreeNode[], query: string): TreeNode[] {
	return nodes.flatMap(node => {
		const step = lastStep(node);
		const selfMatches = `${step.field.name} ${String(step.value)}`.toLowerCase().includes(query);
		const children = filterTree(node.children, query);
		if (selfMatches) return [node];
		return children.length > 0 ? [{ ...node, children }] : [];
	});
}

function valueNodes(node: TreeNode): TreeNode[] {
	return [...(node.ownCount > 0 ? [node] : []), ...node.children.flatMap(valueNodes)];
}

function openableChain(children: TreeNode[]): DimensionValue[] | undefined {
	return children.find(child => child.ownCount > 0)?.chain;
}

function childrenLabel(count: number): string {
	return translateToString(`multidimensional_assortments.children_count_${count === 1 ? 'one' : 'other'}`, [['count', count.toString()]]);
}

function stopSummaryToggle(e: React.MouseEvent, action: () => void) {
	e.preventDefault();
	e.stopPropagation();
	action();
}

function DeleteConfirmation(props: { count: number, message: string, onBreak: () => void, onOk: () => void }) {
	const [typed, setTyped] = React.useState('');
	const expected = props.count.toString();
	return <FlexDiv flow='column' align='stretch' gap='12px'>
		<div>{props.message}</div>
		<Input inputStyle={creationPopupInputStyle} name='delete_count' type='text' placeholder={expected} value={typed} onChange={setTyped} />
		<FlexDiv gap='8px' justify='flex-end'>
			<DefaultButton margin='0' buttonStyle={ButtonStyle.White} onClick={props.onBreak}>{translateToString('cancel')}</DefaultButton>
			<DefaultButton margin='0' buttonStyle={ButtonStyle.Error} disabled={typed.trim() !== expected} onClick={props.onOk}>{translateToString('delete')}</DefaultButton>
		</FlexDiv>
	</FlexDiv>;
}

function RowCheckbox(props: { checked: boolean, indeterminate: boolean, onChange: () => void }) {
	return <BoolCheckbox
		innerRef={(el: HTMLInputElement | null) => {
			if (el) el.indeterminate = props.indeterminate;
		}}
		type='checkbox'
		checked={props.checked}
		onChange={props.onChange}
	/>;
}

const TreeRow = React.memo(function TreeRow(props: { node: TreeNode, level: number, forceOpen: boolean, selected: Set<string>, onToggle: (node: TreeNode) => void, onOpen: (chain: DimensionValue[]) => void, onDelete: (node: TreeNode) => void }) {
	const { node, level, forceOpen, selected, onToggle, onOpen, onDelete } = props;
	const step = lastStep(node);
	const selectable = valueNodes(node);
	const selectedCount = selectable.filter(n => selected.has(n.key)).length;
	const openChain = openableChain(node.children) ?? (level === 0 && node.ownCount > 0 ? node.chain : undefined);
	const row = <Row level={level}>
		<RowCheckbox checked={selectable.length > 0 && selectedCount === selectable.length} indeterminate={selectedCount > 0 && selectedCount < selectable.length} onChange={() => onToggle(node)} />
		<ChevronSlot>{node.children.length > 0 && <ChevronRight className='chevron' size={16} />}</ChevronSlot>
		<Chips><Chip>{step.field.name}<b>{String(step.value)}</b></Chip></Chips>
		{openChain && <RowButton type='button' onClick={e => stopSummaryToggle(e, () => onOpen(openChain))}>{translateToString('multidimensional_assortments.open')}<ExternalLink size={12} /></RowButton>}
		{node.ownCount > 0 && <TrashButton type='button' title={translateToString('delete')} onClick={e => stopSummaryToggle(e, () => onDelete(node))}><Trash2 size={14} /></TrashButton>}
		<ChildrenBadge
			bgColor={node.children.length > 0 ? PastelPrimaryColor : PrimaryColor}
			color={node.children.length > 0 ? DarkGreySidely2 : 'white'}
			title={childrenLabel(node.children.length)}>
			{node.children.length > 0 ? node.children.length : node.ownCount.toString()}
		</ChildrenBadge>
	</Row>;
	if (node.children.length === 0) return row;
	return <Details open={forceOpen || undefined}>
		<summary>{row}</summary>
		{node.children.map(child => <TreeRow key={child.key} node={child} level={level + 1} forceOpen={forceOpen} selected={selected} onToggle={onToggle} onOpen={onOpen} onDelete={onDelete} />)}
	</Details>;
});

function AssortmentFieldPage(props: {
	path: AdditionalValuesPath,
	views: AdditionalValuesView[],
	loading: boolean,
	fieldsMap: Map<number, Field>,
	onOpen: (chain: DimensionValue[]) => void,
	onDelete: (chains: DimensionValue[][]) => Promise<unknown>,
}) {
	const { path, views, loading, fieldsMap, onOpen, onDelete } = props;
	const alert = useAlert();
	const matched = React.useMemo(() => views.flatMap(view => matchingPath(view, [path]) ? [{ view, path }] : []), [views, path]);
	const tree = React.useMemo(() => buildTree(matched, fieldsMap), [matched, fieldsMap]);
	const search = useRecoilValue(AInputSearch);
	const [query, setQuery] = React.useState('');
	React.useEffect(() => {
		const timeout = setTimeout(() => setQuery(search.trim().toLowerCase()), 250);
		return () => clearTimeout(timeout);
	}, [search]);
	const visibleTree = React.useMemo(() => query ? filterTree(tree, query) : tree, [tree, query]);
	const [selected, setSelected] = React.useState<Set<string>>(new Set());
	React.useEffect(() => setSelected(new Set()), [tree]);
	const selectedNodes = React.useMemo(() => tree.flatMap(valueNodes).filter(node => selected.has(node.key)), [tree, selected]);
	const toggle = React.useCallback((node: TreeNode) => setSelected(previous => {
		const next = new Set(previous);
		const nodes = valueNodes(node);
		if (nodes.every(n => previous.has(n.key))) nodes.forEach(n => next.delete(n.key));
		else nodes.forEach(n => next.add(n.key));
		return next;
	}), []);
	const confirmDelete = React.useCallback((title: string, count: number, message: string, chains: DimensionValue[][]) => alert({
		title,
		content: (onBreak, onOk) => <DeleteConfirmation count={count} message={message} onBreak={onBreak} onOk={() => onOk(undefined)} />,
		noButtons: true,
	}).then(res => {
		if (res.res === AlertRes.Ok) onDelete(chains);
	}), [alert, onDelete]);
	const requestDelete = React.useCallback((node: TreeNode) => {
		const combination = node.chain.map(step => `${step.field.name}: ${String(step.value)}`).join(' › ');
		const count = node.ownCount.toString();
		confirmDelete(
			translateToString('multidimensional_assortments.delete_values_title'),
			node.ownCount,
			translateToString('multidimensional_assortments.delete_values_content', [['count', count], ['combination', combination]]),
			[node.chain]
		);
	}, [confirmDelete]);
	const requestDeleteSelection = React.useCallback(() => {
		const count = selectedNodes.reduce((acc, node) => acc + node.ownCount, 0);
		confirmDelete(
			translateToString('multidimensional_assortments.delete_selection_title'),
			count,
			translateToString('multidimensional_assortments.delete_selection_content', [['count', count.toString()], ['rows', selectedNodes.length.toString()]]),
			selectedNodes.map(node => node.chain)
		);
	}, [confirmDelete, selectedNodes]);

	return <FlexDiv flow='column' align='stretch' gap='12px' height='100%' width='100%'>
		<FieldTitle>{path.label}</FieldTitle>
		<Separator />
		<Card>
			{!loading && visibleTree.length === 0 && <NoData message={translateToString('multidimensional_assortments.no_combination')} isNoResult={query.length > 0} height='100%' />}
			{visibleTree.map(node => <TreeRow key={`${node.key}:${query.length > 0}`} node={node} level={0} forceOpen={query.length > 0} selected={selected} onToggle={toggle} onOpen={onOpen} onDelete={requestDelete} />)}
		</Card>
		{selectedNodes.length > 0 && <BottomActionButtonsWrapperWrapper>
			<BottomActionButtonsWrapper round onClick={() => setSelected(new Set())} titleHover={translateToString('clear_selection')}>
				<X />
			</BottomActionButtonsWrapper>
			<BottomActionButtonsWrapper>
				<NumberContainer>{selectedNodes.length}</NumberContainer>
				<p style={{ margin: 0, fontWeight: 600 }}>{translateToString('selected')}</p>
			</BottomActionButtonsWrapper>
			<BottomActionButtonsWrapper round titleHover={translateToString('delete_selection')} onClick={requestDeleteSelection}>
				<Trash2 color={RedSidely} />
			</BottomActionButtonsWrapper>
		</BottomActionButtonsWrapperWrapper>}
	</FlexDiv>;
}

export default React.memo(AssortmentFieldPage);
